import {Form, FormField, FormSubmitHandler} from 'platform/components';
import {Box, Space, VStack} from 'platform/foundation';

import {isNil, isNotNil} from 'ramda';

import {
  GetLabourUnitResponse,
  usePatchLabourUnitMutation,
  usePostLabourUnitMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {settingsRoutes} from '@dms/routes';
import {SettingsFooter} from '@dms/settings/shared';
import {handleApiError} from '@dms/shared';

import {ApiError, Nullish, RequiredTestIdProps, suffixTestId, useNavigate} from 'shared/web';

import {WorkshopUnitForm, WorkshopUnitFormSchema} from '../types/WorkshopUnitForm';

export interface WorkshopUnitsDetailFormProps extends RequiredTestIdProps {
  labourUnit: GetLabourUnitResponse | Nullish;
}

export function WorkshopUnitsDetailForm(props: WorkshopUnitsDetailFormProps) {
  const navigate = useNavigate();

  const [postLabourUnit] = usePostLabourUnitMutation();
  const [patchLabourUnit] = usePatchLabourUnitMutation();

  const handleNavigateBack = () => {
    navigate(settingsRoutes.workshopUnits);
  };

  const handleSubmit: FormSubmitHandler<WorkshopUnitForm> = async (
    formValues,
    _setErrors,
    _reset,
    blockerContext
  ) => {
    if (isNotNil(props.labourUnit)) {
      return await patchLabourUnit({
        labourUnitId: props.labourUnit.labourUnitId,
        body: {
          name: formValues.name,
        },
      })
        .unwrap()
        .then(() => {
          if (blockerContext?.wasSubmittedFromBlocker) {
            return;
          }
          handleNavigateBack();
        })
        .catch((e: ApiError) =>
          handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
        );
    }

    await postLabourUnit({
      name: formValues.name,
    })
      .unwrap()
      .then(() => {
        if (blockerContext?.wasSubmittedFromBlocker) {
          return;
        }
        handleNavigateBack();
      })
      .catch((e: ApiError) =>
        handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
      );
  };

  const defaultValues: Partial<WorkshopUnitForm> = {
    name: props.labourUnit?.name,
  };

  return (
    <Form<WorkshopUnitForm>
      experimentalZodSchema={WorkshopUnitFormSchema}
      defaultValues={defaultValues}
      onSubmit={handleSubmit}
      shouldWatchForUnsavedChanges
    >
      {(control, _formApi, navigateWithoutBlocker) => (
        <VStack spacing={4} data-testid={props['data-testid']}>
          <Box width={100}>
            <FormField
              type="text"
              name="name"
              label={i18n.t('general.labels.name')}
              control={control}
              isRequired
              data-testid={suffixTestId('inputs.name', props)}
            />
          </Box>
          <Space fillAvailable />
          <SettingsFooter
            actions={[
              {
                type: 'button',
                variant: 'secondary',
                title: isNil(props.labourUnit)
                  ? i18n.t('general.actions.discard')
                  : i18n.t('general.actions.discardChanges'),
                onClick: () => navigateWithoutBlocker(settingsRoutes.workshopUnits),
                'data-testid': suffixTestId('actions.discard', props),
              },
              {
                type: 'form-button',
                control,
                buttonType: 'submit',
                variant: 'primary',
                title: isNil(props.labourUnit)
                  ? i18n.t('general.actions.save')
                  : i18n.t('general.actions.saveChanges'),
                'data-testid': suffixTestId('actions.submit', props),
              },
            ]}
          />
        </VStack>
      )}
    </Form>
  );
}
